import $ from 'jquery';
import h from 'hyperscript';
import 'select2/dist/js/select2.full.js';
import removeIcon from '@disy/cadenza-icons/close-small.svg';

import type { Content } from 'ui/content';
import type { PopupParent } from 'ui/popup/popup';
import { getPopupParent } from 'ui/popup/popup';
import { getDefaultResultAdapter } from 'ui/select/custom-results-adapter';
import { initializeMoreLess } from 'ui/select/select2-more-less-links';
import type { FormGroup, ValidationMessages } from 'ui/form-group/form-group';
import { setValidationMessages } from 'ui/form-group/form-group';

import { throttle } from 'cadenza/utils/throttle';
import { uniqueId } from 'cadenza/utils/unique-id';
import { logger } from 'cadenza/utils/logging';
import { assert } from 'cadenza/utils/custom-error';
import type { EventKey } from 'cadenza/utils/event-util';
import { ChangeEvent, on, unByKey } from 'cadenza/utils/event-util';
import { markSubstring } from 'cadenza/utils/mark-substring';
import type { Icon } from 'cadenza/utils/icon/icon';
import { icon } from 'cadenza/utils/icon/icon';
import { px } from 'cadenza/utils/px';
import { isPrimitive } from 'cadenza/utils/primitive';
import type { Nullable } from 'cadenza/utils/type-util';

import type { IdTextPair, OptGroupData, OptionData, SearchOptions } from 'select2';

import './select.css';
import i18n from './messages.properties';

/**
 * The 'amd' property may not be present on the select2 object, this is why we're using optional chaining.
 * For example in our test environment, the amd property is not present. In the browser it is present nevertheless.
 *
 * @param name - the name of the module to load
 * @return - the loaded select module
 */
export const select2Require = (name: string) => $.fn.select2?.amd.require(name);
/**
 * The select2.full bundle that we use includes all modules. Hence, they are "required"/resolved
 * synchronously (not lazy loaded).
 * Those objects will be created only if the select2 object already exists (not loaded in the
 * tests for example)
 */
const Select2Utils = select2Require('select2/utils');
const Select2ArrayData = select2Require('select2/data/array');

const Select2SelectionSearch = select2Require('select2/selection/search');

if (Select2SelectionSearch) {
  const previousBind = Select2SelectionSearch.prototype.bind;
  // Select2 removes listeners, which open the dropdown on ArrowDown.
  // We want the ArrowDown, so we prevent the listener from being removed.
  Select2SelectionSearch.bind = function (...params: unknown[]) {
    const result = previousBind.apply(this, params);
    this.$selection.off('input.searchcheck');
    return result;
  };
}

const COMPONENT_NAME = 'd-select';

const RESULTS_LIMIT = 100;

interface MessagesType {
  loadingMore: () => string;
  maximumSelected: (options: { maximum: number }) => string;
  noResults: () => string;
  removeItem: () => string;
  searching: () => string;
  tooManyOptions: () => string;
}

/**
 An Object of data which will define the node element Option. One can store additional data which
 will be also returned by the selection getter.
 */
export interface SelectOption<DATA = unknown> {
  text: string;
  id: string;
  addSeparationLineBelow?: boolean;
  icon?: Icon;
  selected?: boolean;
  disabled?: boolean;
  data?: DATA;
}

export interface SelectOptionGroup<DATA = unknown> {
  text: string;
  children: SelectOption<DATA>[];
  addSeparationLineBelow?: boolean;
}

export type SelectItem<DATA = unknown> = SelectOption<DATA> | SelectOptionGroup<DATA>;

export type QueryType = (params: SelectQueryParams, callback: (cbkArgs: SelectQueryCallbackArgs) => void) => void;

const DEFAULT_MESSAGES: MessagesType = {
  loadingMore: () => i18n('loadMore'),
  maximumSelected: ({ maximum }) => i18n('selectionTooBig', { count: maximum }),
  noResults: () => i18n('noMatches'),
  removeItem: () => i18n('removeItem'),
  searching: () => i18n('searching'),
  tooManyOptions: () => i18n('tooManyOptions', { limit: RESULTS_LIMIT })
};

const DROPDOWN_MARGIN_BOTTOM = 12;
const MINIMUM_DROPDOWN_HEIGHT = 180;

const PLACEHOLDER_ID = '';
const PLACEHOLDER_TEXT = '$$_placeholder_value_$$';

/**
 * Feedback messages used by select.
 *
 * @typedef {object} customMessages - Object containing function calls for retrieving custom
 *   messages for select2
 * @property {Function} [errorLoading] - Displayed if results could not be loaded.
 * @property {Function} [inputTooLong] - Displayed if user input is too long.
 * @property {Function} [inputTooShort] - Displayed if user input is too short.
 * @property {Function} [loadingMore] - Displayed if pagination is active and more results are
 *   loading. (Dropdown bottom)
 * @property {Function} [maximumSelected] - Displayed if maximum items are selected.
 * @property {Function} [noResults] - Displayed if there are no results.
 * @property {Function} [searching] - Displayed when first results are loaded.
 * @property {Function} [removeAllItems] - Tooltip and aria-label for remove button.
 * @property {Function} [removeItem] - Tooltip and aria-label for remove button.
 */

/**
 * @callback templateSelection
 * @param {object} item - The data object which was appended to Option. Contains information like
 *   id, text, ...
 * @param {Element} container - The container (pill if multiple) which will contain option data
 */

/**
 * @callback templateResult
 * @param {object} item - The data object which was appended to Option. Contains information like
 *   id, text, ...
 * @param {Element} container - The dropdown list element which will contain option data
 */

type CustomContent = JQuery<Node> | Content | null;

export type SelectSorterType = (data: (OptGroupData | OptionData | IdTextPair)[]) => (OptGroupData | OptionData | IdTextPair)[];

/** A select box component's options. */
export interface SelectInitializationOptions<DATA = unknown> {
  /** Element to append on the left side */
  elementLeft?: HTMLButtonElement;
  /** Element to append on the right side */
  elementRight?: HTMLButtonElement;
  /** Data for options */
  data?: SelectItem<DATA>[];
  dropdownAdapter?: unknown;
  resultsAdapter?: unknown;
  /** Adds additional CSS classes to the dropdown container. */
  dropdownStyleClass?: string;
  /** [dropdownParent='dialog-or-body'] - Allows you to customize placement of the dropdown. */
  dropdownParent?: PopupParent;
  /** If true search input field is visible in dropdown. */
  dropdownSearch?: boolean;
  /** Function used for custom search matching */
  matcher?: (params: SearchOptions, data: SelectItem<DATA>) => SelectItem<DATA> | null;
  /**
   * [maxSelection=0] - Multi-value select boxes can set restrictions regarding the
   * maximum number of options that can be selected.  If the value of this option is less than 1,
   * the number of selected items will not be limited.
   */
  maxSelection?: number;
  /** Feedback messages used by select. */
  messages?: Partial<MessagesType>;
  /** [minimumInputLength=0] - Minimum number of characters required to start a search. */
  minimumInputLength?: number;
  /**
   * [moreLessLinks=true] - If true and too many items are selected then overflow
   * will be hidden and show more button will be displayed
   */
  moreLessLinks?: boolean;
  /** [multiple=false] - If true, element becomes a multi-value select box (tags or pills). */
  multiple?: boolean;
  /** Specifies the placeholder for the control. */
  placeholder?: string | IdTextPair;
  /**
   * A function that gets called on every keystroke. Can be used to query
   * the server (throttled) to get options based on user input.
   */
  query?: QueryType;
  /** [queryDelay=300] - Throttle time in ms. */
  queryDelay?: number;
  /**
   * If true no dropdown is shown. User is not permitted to add
   * more items. User can only remove given items.
   */
  removeOnly?: boolean;
  /** Whether the component is disabled. */
  disabled?: boolean;
  /** A value is required for the form to be submittable. */
  required?: boolean;
  /** Name of CSS class to add to the element. */
  styleClass?: string;
  /**
   * In addition to a pre-populated menu of options, select can dynamically create new options from
   * text input by the user in the search box. This feature is called "tagging". To enable tagging,
   * set the tags option to `true`.
   */
  tags?: boolean | SelectOption<DATA>[];
  /** Function to validate tags creation */
  createTagFn?: (params: SearchOptions) => ({ id: string; text: string } | null);
  /** Customizes the way that search results are rendered */
  templateResult?: (item: SelectOption<DATA>, searchText: string, container: HTMLElement) => CustomContent;
  /** Customizes the way that search results group headers are rendered */
  templateGroupResult?: (item: SelectOptionGroup<DATA>, searchText: string, container: HTMLElement) => CustomContent;
  /** Customizes the way that selections are rendered. */
  templateSelection?: (selection: SelectOption<DATA>, container: HTMLElement) => CustomContent;
  /** The list of characters that should be used as token separators. */
  tokenSeparators?: string[];
  /** Validation messages that the select component should use. */
  validationMessages?: ValidationMessages;
  /** Function that is used to sort the items */
  sorter?: SelectSorterType;
  /** Value for the "name" attribute of the contained HTMLSelectElement  */
  selectElementName?: string;
}

export interface SelectQueryParams {
  term: string;
  page: number;

  /** 'limit' and 'offset' are together an exclusive alternative to 'page' */
  limit: number;
  offset: number;
}

export interface SelectQueryCallbackArgs {
  pagination?: { more: boolean };
  results: unknown[];
}

/**
 * A select box component
 */
export class Select<DATA = unknown> extends HTMLElement {

  readonly #select: HTMLSelectElement;
  #preventOpenDropdown = false; // Has to be false initially, otherwise dropdown won't open on first click
  readonly #hasQuery;
  #hasData;
  readonly #elementLeft;
  readonly #elementRight;
  #moreLess: { update: () => void; enabled?: boolean; toggleHandler?: () => void } = { update () { /* overwrite */ } };
  readonly #multiple;
  readonly #placeholder;
  readonly #searchInDropdown;
  #labelEventKey: EventKey | EventKey[] = [];
  #tags: boolean | SelectOption<DATA>[];

  constructor ({
    elementLeft,
    elementRight,
    data = [],
    dropdownAdapter,
    resultsAdapter = getDefaultResultAdapter(select2Require, RESULTS_LIMIT),
    dropdownStyleClass = '',
    dropdownParent = 'dialog-or-body',
    dropdownSearch,
    matcher,
    maxSelection,
    messages,
    minimumInputLength = 0,
    moreLessLinks = false,
    multiple = false,
    placeholder,
    query,
    queryDelay = 300,
    removeOnly = false,
    disabled = false,
    required = false,
    styleClass,
    tags = false,
    templateResult: customTemplateResult,
    templateGroupResult: customTemplateGroupResult,
    templateSelection: customTemplateSelection,
    tokenSeparators,
    createTagFn = getDefaultCreateTagFn(tokenSeparators),
    validationMessages,
    sorter,
    selectElementName = ''
  }: SelectInitializationOptions<DATA> = {}) {
    super();
    this.#hasQuery = Boolean(query);
    this.#hasData = Boolean(data.length);

    assert((!(this.#hasQuery && this.#hasData)), 'data and query cannot be given at the same time.');
    assert(!(this.#hasQuery && removeOnly), 'removeOnly and query cannot not be given at the same time.');
    assert(!customTemplateSelection || (typeof customTemplateSelection === 'function'), 'formatSelection must be a function.');
    assert(!customTemplateResult || (typeof customTemplateResult === 'function'), 'formatResult must be a function.');
    assert(!customTemplateGroupResult || (typeof customTemplateGroupResult === 'function'), 'customTemplateGroupResult must be a function.');
    assert(Array.isArray(data), 'data must be an array.');
    assert(!tokenSeparators || tags, 'tags must not be false if tokenSeparators is defined.');

    if (styleClass) {
      this.classList.add(styleClass);
    }

    this.#multiple = multiple;
    this.#moreLess.enabled = moreLessLinks;

    const select2data = data.filter(item => item != null).map(item => prepareValueForSelect2(item));

    this.#placeholder = placeholder;
    // Empty option, because it is used for injecting placeholder
    this.#select = h(
      `select${multiple ? '.form-group-control' : ''}`,
      { multiple, required, name: selectElementName },
      placeholder ? createPlaceholderOption() : undefined);

    messages = Object.assign({}, DEFAULT_MESSAGES, messages);

    setValidationMessages(this.#select, { valueMissingMessage: i18n('valueMissingMessage'), ...validationMessages });

    if (disabled) {
      this.disabled = disabled;
    }

    on(this.#select, [ 'invalid' ], () => {
      this.classList.toggle('is-invalid', true);
    });

    const templateResult = (item: SelectItem<DATA>, container: HTMLElement) => {
      // do nothing for status message searching
      if (DEFAULT_MESSAGES.searching() === item.text) { return item.text; }

      const isGroup = isGroupItem(item);

      // for multiple selection, do not display already selected items in the dropdown
      if (!isGroup && this.#multiple && item.id && this.selection.find(element => element.id === item.id)) {
        /*
        * Select2 renders also selected option. Select2 sets css to 'display: none' if we return null.
        * When we traverse over option highlight would disappear when hitting selected option.
        * To avoid this, we remove select2-results__option--selectable class.
        */
        container.classList.remove('select2-results__option--selectable');
        return null;
      }

      if (item.addSeparationLineBelow) {
        container.classList.add('select2-results__option--with-separation-line-below');
      }

      const searchText = this.searchInput?.value ?? '';
      let result;
      if (customTemplateResult && !isGroup) {
        result = customTemplateResult(item, searchText, container);
      } else if (customTemplateGroupResult && isGroup) {
        result = customTemplateGroupResult(item, searchText, container);
      } else {
        result = defaultTemplateResult(item, searchText, container);
      }
      container.hidden = !isGroup && isPlaceholderOption(item); // Hide the empty placeholder from selection options
      return result;
    };

    const templateSelection = (item: SelectOption<DATA>, $container: JQuery) => {
      let content: (string | Node | JQuery)[];
      if (isPlaceholderOption(item)) {
        content = [];
      } else if (customTemplateSelection && $container) {
        const customContent = customTemplateSelection(item, $container[0]);
        content = typeof customContent === 'string'
          ? Select2Utils.escapeMarkup(customContent)
          : [ customContent ];
      } else {
        content = defaultTemplateSelection(item, $container);
      }

      if (multiple) {
        $container.find('.select2-selection__choice__display').addClass('ellipsis');
        const remove = $container.find('.select2-selection__choice__remove');
        remove.addClass([ 'button', 'button-icon', 'button-borderless', 'button-s' ])
          .empty()
          .append(icon(removeIcon));
        $container.append(remove); // Position remove button as last element in $container
      }

      return content;
    };

    this.#searchInDropdown = !multiple && (dropdownSearch || this.#hasQuery);

    // Chrome DevTools bug https://bugs.chromium.org/p/chromium/issues/detail?id=659515
    // we have to extract cssClass variables
    const selectionCssClass = `${COMPONENT_NAME}--selection`;
    const dropdownStyleClasses = [
      `${COMPONENT_NAME}--dropdown`,
      ...dropdownStyleClass ? [ dropdownStyleClass ] : [],
      ...removeOnly ? [ 'hidden' ] : [],
      ...this.#searchInDropdown ? [ `${COMPONENT_NAME}--dropdown-search` ] : []
    ];
    let dataAdapter = this.#hasQuery
      ? createDataAdapter.call(this, removeOnly, query!, queryDelay)
      : Select2ArrayData;

    if (tags) {
      const Select2Tags = select2Require('select2/data/tags');
      dataAdapter = Select2Utils.Decorate(dataAdapter, Select2Tags);
      if (Array.isArray(tags)) {
        tags = tags.filter(item => item != null).map(tag => prepareValueForSelect2(tag)) as SelectOption<DATA>[];
      }
    }
    this.#tags = tags;

    if (tokenSeparators != null) {
      const Select2Tokenizer = select2Require('select2/data/tokenizer');
      dataAdapter = Select2Utils.Decorate(dataAdapter, Select2Tokenizer);
    }
    if (minimumInputLength > 0) {
      const MinimumInputLength = select2Require('select2/data/minimumInputLength');
      dataAdapter = Select2Utils.Decorate(dataAdapter, MinimumInputLength);
    }

    if (maxSelection != null && maxSelection > 0) {
      if (multiple) {
        const MaximumSelectionLength = select2Require('select2/data/maximumSelectionLength');
        dataAdapter = Select2Utils.Decorate(dataAdapter, MaximumSelectionLength);
      } else {
        // Do not show "maximum selection reached" message in the dropdown of single selects.
        maxSelection = undefined;
      }
    }

    /* eslint-disable @typescript-eslint/ban-ts-comment */
    $(this.#select).appendTo(this).select2({
      // @ts-ignore
      ajax: this.#hasQuery ? true : undefined, // needed to enable pagination, although we use custom AJAX (see dataAdapter option),
      allowClear: false,
      // @ts-ignore
      data: select2data,
      dataAdapter,
      dropdownAdapter,
      resultsAdapter,
      dropdownCssClass: dropdownStyleClasses.join(' '),
      // @ts-ignore dropdownParent is resolved into an HTMLElement when the select is added to the DOM in the connectedCallback
      dropdownParent,
      language: messages,
      // @ts-ignore
      matcher,
      maximumSelectionLength: maxSelection,
      minimumInputLength,
      multiple,
      placeholder,
      selectionCssClass,
      selectOnClose: !!tags, // If 'tagging' (creation of new dynamic options) is allowed, create an option from the current text on focus lost. selectOnClose module is patched by Disy see Cadenza_Web_Frontend/patches/select2+<version>.patch
      // @ts-ignore
      tags,
      // @ts-ignore
      templateResult,
      // @ts-ignore
      templateSelection,
      tokenSeparators,
      width: '100%',
      sorter,
      createTag: createTagFn
    });

    this._select2.on('results:message', (messageEvent: { message: string; args: { maximum?: number } }) =>{
      const { message, args } = messageEvent;
      switch (message) {
        case 'maximumSelected':
          // Throws 'maximum-selected' event to be handled from outside when the select2 native 'results:message' with 'maximumSelected' message is thrown
          // This happens when there is maximum number of values defined (maxSelection) and reached.
          this.dispatchEvent(new CustomEvent('maximum-selected', { detail: args.maximum }));
      }
    });
    /* eslint-enable @typescript-eslint/ban-ts-comment */

    /*
     * h('select', options) sets the first option as selected even if  new Option(text, id, false, false) was set
     * select2 ignores placeholder if some option is set selected, therefore we have to add dummy option as first option and remove it after select2 is initialized
     */
    if (multiple && placeholder) {
      this.#select.firstElementChild?.remove();
    }

    if (elementLeft) {
      this.#elementLeft = elementLeft;
      elementLeft.classList.add(`${COMPONENT_NAME}--element-left`);
      this.classList.add('has-element-left');
      this.append(elementLeft);
    }

    if (elementRight) {
      this.#elementRight = elementRight;
      elementRight.classList.add(`${COMPONENT_NAME}--element-right`);
      this.classList.add('has-element-right');
      this.append(elementRight);
    }

    let dispatching = false;
    $(this).on('change', (event) => {
      const target = event.target as HTMLElement;
      if (target === this.#select) {
        // must be canceled explicitly
        event.stopImmediatePropagation();
        this.classList.toggle('is-invalid', false);
        this.#moreLess.update();
        if (tags && multiple) {
          this._removeDuplicateTags();
        }
        if (!dispatching) {
          this.dispatchEvent(new ChangeEvent());
        }
      } else {
        // FormGroup identifies the accessibleSelect as control. For the resettable option
        // to work, dispatch a change event on the accessible #select.
        try {
          dispatching = true;
          this.#select.dispatchEvent(new ChangeEvent());
        } finally {
          dispatching = false;
        }
        return;
      }
    });

    const select = $(this.#select)
      // Unselecting opens DropDown. This is unwanted.
      .on('select2:unselect', () => {
        this._preventOpenDropdown();
      })
      .on('select2:opening', event => {
        if (this._isPreventOpenDropdown) {
          event.preventDefault();
        }
      })
      .on('select2:open', () => {
        this.#updateDropdownVisibility();
        if (this.#searchInDropdown) {
          const searchInput = this.searchInput!;
          searchInput.classList.add('form-control');
          searchInput.focus();
        }

        const dropdownElem = document.querySelector('.select2-container--default .select2-results > .select2-results__options') as HTMLElement;
        const dropdownAbove = document.querySelector('.select2-container--default .select2-dropdown--above');
        if (dropdownElem && !dropdownAbove) { // if not already above
          const dropdownTopPos = dropdownElem.getBoundingClientRect().top;
          const footerElem = document.querySelector('.d-page--footer');
          const footerHeight = footerElem ? footerElem.getBoundingClientRect().height : 15; // Fallback to the current default footer height
          const dropdownHeight = window.innerHeight - dropdownTopPos - footerHeight - DROPDOWN_MARGIN_BOTTOM;
          // If space below is too small and dropdown would end up being too small, don't apply the fix. Instead, the
          // dropdown will automatically be shown above of the select (where hopefully more space is available)
          if (dropdownHeight > MINIMUM_DROPDOWN_HEIGHT) {
            dropdownElem.style.maxHeight = px(dropdownHeight);
          }
        }
      });

    // If we are using multiple selection, we need to reposition last chosen option
    // so that we have list of selected items in the order that we selected them.
    // Without this, order of the selected items would be the same of that in the menu.
    // https://github.com/select2/select2/issues/3106#issuecomment-539469984
    if (multiple) {
      select
        .on('select2:select', function (e) {
          const id = e.params.data.id;
          const option = $(e.target).children(`[value='${id}']`);
          $(e.target).append(option).trigger('change.select2');
        });
    }

    if (removeOnly) {
      this.searchInput!.parentElement!.hidden = true; // If removeOnly is true, we also have tags and a search input.
    }

    if (this.#multiple && this.#moreLess.enabled) {
      Object.assign(this.#moreLess, initializeMoreLess(this.#select));
      this.#moreLess.toggleHandler = () => {
        this.close();
      };
      this.#select.addEventListener('more-less-toggle', this.#moreLess.toggleHandler);
    }

    this._bindShortcuts();
  }

  connectedCallback () {
    this.classList.add(COMPONENT_NAME);

    setTimeout(() => { // when light DOM is ready
      const labelledBy = this.labelledBy;
      if (!labelledBy) {
        this.labelledBy = this.closest<FormGroup>('form-group')?.label;
      }

      const label = this.labelledBy && document.getElementById(this.labelledBy);
      if (label) {
        this.#labelEventKey = on(label, 'click', () => this.focus());
      }
    });

    const select2 = this._select2;
    const dropdownParent = select2.options.get('dropdownParent');
    const dropdownParentElement = getPopupParent(dropdownParent, this);
    assert(dropdownParentElement, 'DropdownParent options should not be null');
    select2.dropdown.$dropdownParent = $(dropdownParentElement!);

    // Select2 should handle "Escape" only if the dropdown is open.
    // Needed because select2 does not bubble the "Escape"
    on(this, [ 'keydown', 'keyup' ], (event: KeyboardEvent) => {
      if (event.target !== this && event.key === 'Escape' && !this._select2.isOpen()) {
        event.stopImmediatePropagation();
        // prevent default if inside a popup to prevent closing ancestor dialogs
        if (this.closest('.d-popup')) {
          event.preventDefault();
        }
        this.dispatchEvent(new KeyboardEvent(event.type, { key: 'Escape', bubbles: true }));
      }
    }, { capture: true });
  }

  disconnectedCallback () {
    $(this.#select).select2('close');
    removeSelect2ScrollListeners();
    unByKey(this.#labelEventKey);
  }

  _bindShortcuts () {
    if (this.#multiple) {
      const search = this.searchInput!; // For multi-selection, the search input is present.
      search.addEventListener('keydown', (event: KeyboardEvent) => {
        // Force dispatch 'CTRL + ENTER'
        if (event.ctrlKey && event.key === 'Enter') {
          this.close();
          // Prevent Select2 handling keydown for this special case
          event.stopImmediatePropagation();
          // Select2 handles keyup. It will open the dropdown. In this special case we have to
          // prevent this behaviour
          $(search).one('keyup', (keyupEvent) => {
            keyupEvent.stopImmediatePropagation();
          });

          // Use CustomEvent because IE does not support KeyboardEvent.
          this.dispatchEvent(Object.assign(new CustomEvent('keydown', { bubbles: true }), {
            key: 'Enter',
            ctrlKey: true,
            keyCode: 13,
            location: 0,
            code: 'Enter',
            charCode: 0,
            which: 13
          }));
        }
      });
    }
  }

  #hasUnselectedOption () {
    return [ ...this.#select.getElementsByTagName('option') ].some(option => !option.selected);
  }

  #getDropdownVisibility () {
    // if we want to use the select component without selection functionality, we hide the dropdown
    if (!!this.#tags && !this.#hasData && !this.#hasQuery) {
      return false;
    }
    if (this.#hasQuery) {
      return true;
    }
    if (!this.#hasData) {
      return false;
    }
    /* For multi-select, we want to hide empty dropdown (all options are selected and there is nothing left to select).
       For single-select, we want to show the dropdown even if there is only one option available and it's selected */
    return this.#multiple ? this.#hasUnselectedOption() : true;
  }

  #updateDropdownVisibility () {
    let showDropdown = this.#getDropdownVisibility();
    this._select2.dropdown.$dropdown.toggleClass('hidden', !showDropdown);
  }

  open () {
    $(this.#select).select2('open');
  }

  close () {
    $(this.#select).select2('close');
  }

  override focus () {
    this._select2.focus();
  }

  /**
   * Get the selection
   *
   * @return The selected options
   */
  get selection (): SelectOption<DATA>[] {
    return $(this.#select).select2('data') as unknown as SelectOption<DATA>[];
  }

  /**
   * Sets options based on given data.
   *
   * Options will be set based on given data. If data is a string or an array of strings
   * then it is seen as ID or a flat array of IDs otherwise data must contain all
   * relevant option data.
   *
   * @param data - ID, array of IDs or array of option data
   */
  set selection (data: unknown | SelectOption<DATA> | (unknown | SelectOption<DATA>)[]) {
    if (!this.#multiple && !isPrimitive(data) && (Array.isArray(data) && data.length > 1)) {
      logger.error('Single select cannot set more than one value');
      return;
    }

    if (!Array.isArray(data)) {
      data = [ data ];
    }
    const preparedData = (data as unknown[]).filter(item => item != null).map(prepareValueForSelect2) as SelectOption[];

    for (const option of preparedData) {
      if (!this.#select.querySelector(`option[value='${option.id}']`)) {
        const newOption = createOptionForSelect2(option);
        this.#select.add(newOption);
      }
    }

    if (this.#hasData) {
      const options = [ ...this.#select.getElementsByTagName('option') ];
      const selectedOptions: HTMLOptionElement[] = [];
      const notSelectedOptions: HTMLOptionElement[] = [];
      options.forEach(option => {
        const selected = preparedData.some(item => (item.id === option.value));
        option.selected = selected;
        if (selected) {
          selectedOptions.push(option);
        } else {
          notSelectedOptions.push(option);
        }
      });
      const reorderedSelectedOptions = this._calculateReorderedOptions(preparedData, selectedOptions);
      if (reorderedSelectedOptions) {
        options.forEach(option => this.#select.removeChild(option));
        reorderedSelectedOptions.forEach(option => this.#select.appendChild(option));
        notSelectedOptions.forEach(option => this.#select.appendChild(option));
      }
      // Programmatically change of options property selected does not notify select2 about changes
      $(this.#select).trigger('change.select2'); // Notify only Select2 of changes
    } else {
      this.#select.innerHTML = '';
      if (this.#multiple && this.#placeholder) {
        this.#select.add(createPlaceholderOption()); // Empty option, because it is used for injecting placeholder
      }
      preparedData.forEach(item => {
        const newOption = new Option(item.text, item.id, true, true);
        Select2Utils.StoreData(newOption, 'data', item);
        this.#select.add(newOption);
      });
    }
  }

  setData (data: SelectItem<DATA>[]) {
    this.#hasData = Boolean(data.length);
    $(this.#select).val(''); // clean up current selection
    this.#select.innerHTML = ''; // clean up current data (<option> / <optGroup> elements)

    data.filter(item => item != null)
      .map(item => {
        const preparedItem = prepareValueForSelect2(item);
        return isGroupItem(preparedItem)
          ? createOptionGroupForSelect2(preparedItem)
          : createOptionForSelect2(preparedItem);
      })
      .forEach(item => this.#select.add(item));
  }

  getData () {
    const optionElements = [ ...this.#select.querySelectorAll('option') ];
    return optionElements.map(option => {
      return ({
        id: option.dataset.select2Id,
        text: option.textContent
      });
    });
  }

  // In multiple selection when selecting options programmatically, options should appear
  // in the same order as passed in data. If they are already in a proper order, then we return false
  // here, because there is no need to do anything
  _calculateReorderedOptions (data: SelectOption[], selectedOptions: HTMLOptionElement[]) {
    let shouldReorder = false;
    const reorderedSelectedOptions: HTMLOptionElement[] = [];
    if (selectedOptions.length > 0) {
      for (let i = 0; i < data.length; i++) {
        const row = data[i];
        const option = selectedOptions[i];
        if ((row.id || row) === option.value) {
          reorderedSelectedOptions.push(option);
        } else {
          const reorderedOption = selectedOptions.find(o => (row.id || row) === o.value);
          if (reorderedOption) {
            shouldReorder = true;
            reorderedSelectedOptions.push(reorderedOption);
          }
        }
      }
    }

    return shouldReorder ? reorderedSelectedOptions : false;
  }

  checkValidity () {
    return !this.classList.contains('is-invalid');
  }

  get validity () {
    return this.#select.validity;
  }

  get disabled () {
    return this.classList.contains('is-disabled');
  }

  set disabled (value) {
    if (this.#elementLeft) {
      this.#elementLeft.disabled = value;
    }

    if (this.#elementRight) {
      this.#elementRight.disabled = value;
    }

    const querySelector = this.querySelector('.d-select2--more-less') as HTMLButtonElement;
    if (querySelector) {
      querySelector.disabled = value;
    }

    this.#select.disabled = value;
    this.classList.toggle('is-disabled', value);
  }

  get required () {
    return this.#select.required;
  }

  get labelledBy (): string | null {
    return this.#labelControl.getAttribute('aria-labelledby');
  }

  set labelledBy (elementOrId: Nullable<Element | string>) {
    if (elementOrId instanceof Element && !elementOrId.id) {
      elementOrId.id = uniqueId();
    }

    const id = typeof elementOrId === 'string' ? elementOrId : elementOrId?.id;
    const control = this.#labelControl;
    if (id) {
      control.setAttribute('aria-labelledby', id);
    } else {
      control.removeAttribute('aria-labelledby');
    }
  }

  get #labelControl () {
    return (!this.#searchInDropdown && this.searchInput) || this.accessibleSelect;
  }

  get describedBy () {
    return this._selection.getAttribute('aria-describedby');
  }

  set describedBy (elementOrId) {
    const id = elementOrId?.id ?? elementOrId;
    if (id) {
      this._selection.setAttribute('aria-describedby', id);
    } else {
      this._selection.removeAttribute('aria-describedby');
    }
  }

  get searchInput (): HTMLInputElement | undefined {
    if (this.#searchInDropdown) {
      return this._select2.dropdown.$search?.[0];
    }
    return this._select2.selection.$search?.[0];
  }

  get accessibleSelect () {
    return this.#select;
  }

  /**
   * Returns the ID of the element with the combobox role.
   *
   * @return The ID
   */
  get comboboxId () {
    if (!this._selection.id) {
      this._selection.id = uniqueId();
    }
    return this._selection.id;
  }

  // ============= Private ============ //

  get _selection () {
    return this._select2.$selection[0];
  }

  get _select2 () {
    return $(this.#select).data().select2;
  }

  _preventOpenDropdown () {
    this.#preventOpenDropdown = true;
    setTimeout(() => { this.#preventOpenDropdown = false; });
  }

  get _isPreventOpenDropdown () {
    return this.#preventOpenDropdown;
  }

  /*
  * There is a bug in select2 which causes 2 tags being created when user types something and presses Enter.
  * Precondition: tags and multiple enabled, data or query also available.
  * Reproduce: Assume there is one option available: "Apple". If user types "app" and adds it,
  * tag "app" is created. If the user types "app" again, dropdown shows only "Apple". If user presses
  * enter then 2 tags are created: "app" and "Apple". This results in duplicated tag "app".
  * Problem seems to be with select2:
  * https://github.com/select2/select2/issues/3498
  * https://github.com/select2/select2/issues/5861
  *
  * This is fix for our case: we know we cannot have duplicated email addresses, so we can safely
  * take last dynamically created tag and check whether there is another one with same value.
  * In case we have match we remove the last one. Other tags which are created by selecting
  * option from dropdown don't have selector: "[data-select2-tag="true"]".
  */
  _removeDuplicateTags () {
    const newTags = [ ...this.#select.querySelectorAll('option[data-select2-tag="true"]') ] as HTMLOptionElement[];
    const lastTag = newTags.pop();
    if (newTags.length > 0 && lastTag && newTags.some(tag => tag.value === lastTag.value)) {
      lastTag.remove();
    }
  }

  /**
   * If the select is initialized with the 'query' option, this is the way to programmatically trigger a query.
   * If this select contains a searchInput, its value is included in the query.
   */
  executeQuery () {
    if (this.#hasQuery) {
      let term;
      if (this.searchInput) {
        term = $(this.searchInput).val();
      }
      this._select2.trigger('query', { term });
    }
  }

  setCustomValidity (error: string) {
    this.#select.setCustomValidity(error);
  }

}

customElements.define(COMPONENT_NAME, Select);

function defaultTemplateSelection (item: SelectOption, $container: JQuery) {
  const content = [];

  // The container might be missing if select2 is initialized with a user defined placeholder and no selected items
  $container?.addClass('default-layout');
  if (item.icon) {
    content.push(item.icon.cloneNode(true));
  }
  content.push(h('span.ellipsis', item.text));

  return content;
}

function defaultTemplateResult (item: SelectItem, searchText: string, container: HTMLElement) {
  container.classList.add('default-layout');
  const iconElement = (item as SelectOption).icon?.cloneNode(true);
  const isGroupName = container.classList.contains('select2-results__group');
  const text = h(`span${isGroupName ? '' : '.ellipsis'}`, searchText ? markSubstring(item.text, searchText) : item.text);
  return iconElement ? [ iconElement, text ] : text;
}

function getDefaultCreateTagFn (tokenSeparators?: string[]) {
  return (params: { term: string }) => {
    const value = params.term.trim();
    return !(value === '' || tokenSeparators?.some(separator => value === separator))
      ? { id: value, text: value }
      : null;
  };
}

// Is called with the select component as this.
function createDataAdapter (removeOnly: boolean, query: QueryType, queryDelay: number) {
  function CustomData ($element: JQuery, options: Record<string, unknown>) {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore TS does not get the Select2Utils.Extend
    CustomData.__super__.constructor.call(this, $element, options);
  }

  Select2Utils.Extend(CustomData, Select2ArrayData);

  if (removeOnly) {
    CustomData.prototype.query = (params: SelectQueryParams, callback: (args: SelectQueryCallbackArgs) => void) => {
      callback({ results: [] });
    };
  } else {
    let currentCallback: ((args: SelectQueryCallbackArgs) => void) | null;
    // `params` has this structure: `{term: "aa", _type: "query", page: 1}` where `term` is the user input
    const throttledQuery = throttle((params: SelectQueryParams, callback: (args: SelectQueryCallbackArgs) => void) => {
      currentCallback = callback; // only latest request must be shown
      if (!params.page) { // initial request page number is undefined
        params.page = 1;
      }
      query(params, (data: SelectQueryCallbackArgs) => {
        if (currentCallback === callback) {
          currentCallback = null; // due to garbage-collector
          callback(data);
        } else {
          logger.warn('Response of a previous query was ignored. Obsolete queries should be aborted.');
        }
      });
    }, queryDelay);
    // Cannot be inlined into wrappedQuery, because of throttling

    /**
     * Get a set of options that are filtered based on the parameters that have been passed on in. Doc from:
     * https://select2.org/advanced/adapters-and-decorators#dataadapter
     *
     * @param params - An object containing any number of parameters that the query could be affected by. Only the core
     * parameters will be documented.
     * @param params.term - A user-supplied term. This is typically the value of the search box, if one exists, but can
     * also be an empty string or null value.
     * @param params.page - The specific page that should be loaded. This is typically provided when working with remote
     * data sets, which rely on pagination to determine what objects should be displayed.
     * @param callback - The function that should be called with the queried results.
     */
    CustomData.prototype.query = function (this: Select, params: SelectQueryParams, callback: (args: SelectQueryCallbackArgs) => void) {
      if (!this._isPreventOpenDropdown) {
        throttledQuery(params, callback);
      }
    };
  }
  return CustomData;
}

/**
 * Function that formats value so that it can be used with select2. It checks whether the value
 * is a primitive, and if that's the case returns object that has the same id and text as the value
 * of the element.
 *
 * @param value - Value that should be used with select2.
 * @return - Returns the value that was parsed to OptionData format.
 */
function prepareValueForSelect2 (value: unknown): SelectItem & { _resultId: string } {
  if (isPrimitive(value)) {
    const valueString = String(value);
    return {
      id: valueString,
      text: valueString,
      _resultId: valueString, // workaround for screenreader accessibility CADENZA-14735
      data: value
    };
  }
  const item = value as ReturnType<typeof prepareValueForSelect2>;
  if ('id' in item) {
    item._resultId = item.id;
  }
  return item;
}

function createOptionForSelect2 (selectOption: SelectOption<unknown>) {
  const element = new Option(selectOption.text, selectOption.id, false, selectOption.selected);
  Select2Utils.StoreData(element, 'data', selectOption);
  return element;
}

function createOptionGroupForSelect2 (selectOptionGroup: SelectOptionGroup<unknown> & { _resultId: string }) {
  const element = h<HTMLOptGroupElement>('optgroup',
    { label: (selectOptionGroup.text || selectOptionGroup) },
    selectOptionGroup.children.map(createOptionForSelect2));
  Select2Utils.StoreData(element, 'data', selectOptionGroup);
  return element;
}

function createPlaceholderOption () {
  return new Option(PLACEHOLDER_TEXT, PLACEHOLDER_ID);
}

/**
 * Select2 disables scrolling when a select list is openend to keep the select and the select list together.
 * In some cases, the Select2 element is removed from the DOM before it can remove its scroll listeners
 * from its parents - then scrolling will remain prevented because it then cannot find its parents.
 * This function removes all scroll listeners from the content panel upwards.
 */
function removeSelect2ScrollListeners () {
  const contextPanel = document.querySelector('#d-analysis-context-panel .d-workbook-panel--content');
  if (contextPanel) { // In worksheet popups we might not have a context panel
    [ $(contextPanel), ...$(contextPanel).parents() ].forEach(a => $(a).off('scroll'));
  }
}

export function isPlaceholderOption (option: SelectOption) {
  return option.id === PLACEHOLDER_ID && option.text === PLACEHOLDER_TEXT;
}

function isGroupItem<DATA> (item: SelectItem<DATA>): item is SelectOptionGroup<DATA> {
  return !!(item as SelectOptionGroup).children;
}
